// react
import { useEffect, useState } from 'react';

// types
import { ActionButton, LoadingStatus, Transaction } from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { useSearchParams } from 'react-router-dom';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// mui
import { Grid } from '@mui/material';

// dialogs
import { DialogActions } from '../DialogActions/DialogActions';

// forms
import { FormAddPaymentRefund } from '../../forms/FormAddPaymentRefund/FormAddPaymentRefund';

// ui
import { formatCurrency } from '../../../utils/currency';
import { Loading } from '../../ui/Loading/Loading';

// props
export interface DialogAddRefundProps {
	isOpen: boolean;
	onClose: () => void;
	onSuccess?: () => void;
}

export const DialogAddRefund = ({ isOpen, onClose, onSuccess }: DialogAddRefundProps) => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// hooks
	const [searchParams] = useSearchParams();
	const { t } = useTranslation();

	// params
	const paramDialogTransaction = searchParams.get('dialogTransaction');

	// state
	const [status, setStatus] = useState<LoadingStatus>('DEFAULT');
	const [transaction, setTransaction] = useState<Transaction | null>(null);

	// fetch transaction
	const { isLoading } = useFetch({
		isEnabled: Boolean(paramDialogTransaction && isOpen),
		url: `${process.env.REACT_APP_API_URL}/transactions/${paramDialogTransaction}?populate=fromAccount,fromEntity,payments,payments.conversions,payments.loan,toEntity`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data && res.data[0]) {
				setTransaction(res.data[0]);
			}
		},
	});

	// reset status when dialog is closed
	useEffect(() => {
		if (!isOpen) {
			setStatus('DEFAULT');
			setTransaction(null);
		}
	}, [isOpen]);

	const actions: ActionButton[] = [
		{
			children: t('cancel'),
			onClick: onClose,
		},
		{
			children: t('recordRefund'),
			form: 'form-add-payment-refund',
			loading: status === 'LOADING',
			type: 'submit',
			variant: 'contained',
		},
	];

	let content = null;

	if (isLoading) {
		content = (
			<Grid item xs={12}>
				<Grid alignItems="center" container justifyContent="center">
					<Grid item>
						<Loading />
					</Grid>
				</Grid>
			</Grid>
		);
	} else if (transaction) {
		content = (
			<FormAddPaymentRefund
				onClose={onClose}
				onSuccess={onSuccess}
				setStatus={setStatus}
				status={status}
				transaction={transaction}
			/>
		);
	}

	return (
		<DialogActions
			actions={actions}
			isOpen={isOpen}
			onClose={onClose}
			subtitle={{
				children: transaction
					? formatCurrency({
							currency: transaction.currency,
							value: transaction.total,
						})
					: null,
			}}
			title={{
				children: t('recordRefund'),
				variant: 'h3',
			}}>
			{content}
		</DialogActions>
	);
};
